/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useState, useEffect, useRef,
} from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TourTooltip from './TourTooltip';
import userActions from '../../../redux/actions/userActions';

const TOUR_STORAGE_KEY = 'guidedTourCompleted';

const allSteps = [
  {
    target: '[data-tour="menu-button"]',
    title: 'Welcome!',
    content: 'Welcome to your learning platform! This button opens the navigation menu. You will find all the tabs: Course Content, Results, Exams, Medflix (videos), Glossary, Learning Links and Help.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="resume-widget"]',
    title: 'Course access',
    content: 'Click here to access your program. The program is divided into chapters that you can browse in any order you wish. Each chapter contains 2 to 4 units.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exams-dates"]',
    title: 'Exam dates',
    content: 'Find your exam dates and semester information here. We recommend studying each chapter unit before attempting the chapter review tests.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="resources-widget"]',
    title: 'Resources',
    content: 'Access additional learning resources here to enhance your learning experience.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="total-time"]',
    title: 'Study time',
    content: 'This widget displays your total connection and study time on the platform.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="total-progress"]',
    title: 'Quiz progress',
    content: 'Track your quiz progress here. You must answer all questions, even if you are unsure. You are not penalised for incorrect answers and can retry as many times as you wish.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="unit-progress"]',
    title: 'Unit progress',
    content: 'Track your unit progress here. When you have completed all exercises in a unit, remember to click "Validate your unit here" then "Submit answers".',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="notifications-button"]',
    title: 'Notifications',
    content: 'Click here to check your notifications and important messages from your institution.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="profile-button"]',
    title: 'Profile',
    content: 'Access your profile and settings here. You can also log out from this menu. Feel free to check the Help tab in the menu if you have any questions. Good luck!',
    placement: 'bottom',
    disableBeacon: true,
  },
];

const useStyles = makeStyles({
  '@global': {
    '.__floater': {
      animation: 'none !important',
      transition: 'opacity 220ms ease !important',
    },
    '.__floater__open': {
      animation: 'none !important',
    },
    '.__floater__closing': {
      animation: 'none !important',
    },
  },
});

function getAvailableSteps() {
  return allSteps.filter((step) => document.querySelector(step.target));
}

const GuidedTour = ({ _id, userData, updateUserNames }) => {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const theme = useTheme();
  const pollRef = useRef(null);
  useStyles();

  useEffect(() => {
    if (userData.tourCompleted) {
      localStorage.setItem(TOUR_STORAGE_KEY, true);
    }
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
    if (tourCompleted) {
      return undefined;
    }

    let attempts = 0;
    pollRef.current = setInterval(() => {
      attempts += 1;
      const available = getAvailableSteps();
      if (available.length >= 2) {
        clearInterval(pollRef.current);
        setSteps(available);
        setStepIndex(0);
        setRun(true);
      }
      if (attempts >= 20) {
        clearInterval(pollRef.current);
      }
    }, 1000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleCallback = (data) => {
    const {
      action, index, type, status,
    } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      updateUserNames(_id, { tourCompleted: true });
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1);
      }
    } else if (action === ACTIONS.CLOSE) {
      setRun(false);
      setStepIndex(0);
      updateUserNames(_id, { tourCompleted: true });
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    }
  };

  return (
    <>
      {steps.length > 0 && (
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        callback={handleCallback}
        scrollToFirstStep
        tooltipComponent={TourTooltip}
        disableScrolling
        floaterProps={{
          disableAnimation: true,
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          open: 'Open',
          skip: 'Skip the tour',
        }}
        styles={{
          options: {
            primaryColor: theme.palette.primary.main,
            zIndex: 10000,
            overlayColor: 'rgba(0,0,0,.45)',
            arrowColor: '#fff',
          },
          spotlight: {
            borderRadius: 12,
          },
        }}
      />
      )}
    </>
  );
};

const mapState = (state) => {
  const { userData } = state.user;
  const { _id } = state.auth.authData;
  return {
    _id, userData,
  };
};

const actionCreators = {
  updateUserNames: userActions.updateUserNames,
};
export default connect(mapState, actionCreators)(GuidedTour);
